import React, { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axiosInstance from "../utils/axiosInstance";
import { EyeIcon } from "@heroicons/react/24/outline";
import { formatStatus, getStatusBadgeClass, truncateText } from "../utils/Utils";
import { AuthContext } from "../context/AuthContext";
import DownloadModFileButton from "../components/DownloadModFileButton";
import { usePagedList } from "../hooks/usePagedList";
import {
  Badge,
  Page,
  PageHeader,
  PageLoader,
  Pagination,
  Panel,
  SearchInput,
  TableShell,
  Td,
  TdEmpty,
  Th,
  Tr,
} from "../components/ui";

const MyFiles = () => {
  const { user } = useContext(AuthContext);
  const [files, setFiles] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFiles = async () => {
      try {
        const response = await axiosInstance.get(`/users/${user.id}/files`);
        setFiles(response.data);
      } finally {
        setLoading(false);
      }
    };

    fetchFiles();
  }, []);

  const filteredFiles = files.filter(
    (file) =>
      file.file_id
        .toString()
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      file.file_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const paged = usePagedList(filteredFiles, 25, searchTerm);

  if (loading) {
    return (
      <Page>
        <PageHeader title="All Files" onBack={() => window.history.back()} />
        <PageLoader label="Loading your files…" />
      </Page>
    );
  }

  return (
    <Page>
      <PageHeader
        title="All Files"
        subtitle={`${files.length} file${files.length === 1 ? "" : "s"} uploaded`}
        onBack={() => window.history.back()}
      >
        <Badge tone="accent">{filteredFiles.length} shown</Badge>
      </PageHeader>

      <SearchInput
        placeholder="Search by file id, file name…"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />

      <Panel padded={false} className="overflow-hidden">
        <TableShell className="rounded-[22px] border-0">
          <thead>
            <tr>
              <Th>File ID</Th>
              <Th>Vehicle</Th>
              <Th>File Name</Th>
              <Th>Credit</Th>
              <Th>Price Status</Th>
              <Th>Status</Th>
              <Th>Feasible</Th>
              <Th align="center">Mod File</Th>
              <Th align="center">View</Th>
            </tr>
          </thead>
          <tbody>
            {filteredFiles.length === 0 ? (
              <TdEmpty colSpan={9}>
                {files.length === 0 ? "You haven't uploaded any files yet." : "No files match your search."}
              </TdEmpty>
            ) : null}
            {paged.visible.map((file) => (
              <Tr key={file.id}>
                <Td className="font-mono text-xs">{file.file_id}</Td>
                <Td className="font-medium text-gray-800 dark:text-slate-200">{file.brand}</Td>
                <Td>{truncateText(file.file_name, 23)}</Td>
                <Td className="font-semibold">{file.price}</Td>
                <Td>
                  <span className={getStatusBadgeClass(file.price_status)}>
                    {formatStatus(file.price_status)}
                  </span>
                </Td>
                <Td>
                  <span className={getStatusBadgeClass(file.status)}>
                    {formatStatus(file.status)}
                  </span>
                </Td>
                <Td>
                  {file.feasible && (
                    <span className={getStatusBadgeClass(file.feasible)}>
                      {formatStatus(file.feasible)}
                    </span>
                  )}
                </Td>
                <Td className="text-center">
                  <DownloadModFileButton
                    fileId={file.id}
                    fileName={file.mod_file_name}
                    status={file.status}
                    price_status={file.price_status}
                    isTable={true}
                  />
                </Td>
                <Td className="text-center">
                  <Link
                    to={`/view-file/${file.id}`}
                    title="View file"
                    className="inline-flex h-8 w-8 items-center justify-center rounded-full text-gray-400 transition hover:bg-red-500/10 hover:text-red-500 dark:hover:text-orange-400"
                  >
                    <EyeIcon className="h-5 w-5" />
                  </Link>
                </Td>
              </Tr>
            ))}
          </tbody>
        </TableShell>
      </Panel>

      <Pagination
        page={paged.page}
        totalPages={paged.totalPages}
        onChange={paged.setPage}
        total={paged.total}
        pageSize={paged.pageSize}
      />
    </Page>
  );
};

export default MyFiles;
